import { LitElement, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { ColumnDetail, ComplexSelectionGroupEvent, SelectionType } from '../types';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { TemplateResult } from 'lit/development';
import { map } from 'lit/directives/map.js';

@customElement('gz-selectable-table-row')
export class GzSelectableTableRowWebComponent<Row extends string, Column extends number> extends LitElement {
  @property({ type: Number, attribute: true, reflect: true })
  selectionType = SelectionType.RADIO;

  @property({ type: Boolean, attribute: true, reflect: true })
  allowRowSelection = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  hasColumnsDisabled = false;

  @property({ type: String, attribute: true, reflect: true })
  row: Row;

  @property({ type: String, attribute: true, reflect: true })
  label = '';

  @property({ type: String, attribute: true, reflect: true })
  tooltip = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  indeterminate = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  checked = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  hidden = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  allowColumnSelection = false;

  @property({ attribute: true, reflect: true })
  value;

  @property({ type: Array, attribute: true, reflect: true })
  columnDetails: ColumnDetail<Column>[];

  @property({ type: Array, attribute: true, reflect: true })
  availableColumns: Column[]; // default all columns

  static get styles() {
    return styles;
  }

  protected render() {
    return html` ${this.renderFirstCell()} ${this.renderContentCells()} `;
  }

  /**
   * Render the first cell of the row.
   * - If row selection is available, a checkbox will be displayed.
   * - Otherwise, only the label will be displayed.
   */
  private renderFirstCell(): TemplateResult {
    return html`<div class="view-cell first-cell">
      <gz-tooltip tooltipText="${this.tooltip}">
        ${this.allowRowSelection
          ? html`<gz-checkbox
              .label="${this.label}"
              ?checked="${this.checked}"
              ?indeterminate="${this.indeterminate}"
              @change="${this.handleRowCheckboxSelectionChange}"
            ></gz-checkbox>`
          : ''}
        ${!this.allowRowSelection
          ? html` <gz-text fontSize="${FontSizes.PARAGRAPH_13}" fontWeight="${FontWeights.NORMAL}"
              >${this.label}</gz-text
            >`
          : ''}
      </gz-tooltip>
    </div>`;
  }

  /**
   * Render the content cells of the row based on the selection type.
   */
  private renderContentCells(): TemplateResult {
    switch (this.selectionType) {
      case SelectionType.RADIO:
        return this.renderRadioGroup();
      case SelectionType.CHECKBOX:
        return this.renderCheckboxGroup();
    }
  }

  /**
   * Render the checkbox group of the row.
   */
  private renderCheckboxGroup(): TemplateResult {
    return html` ${map(this.columnDetails, columnDetail => this.renderCell(columnDetail))} `;
  }

  /**
   * Render the radio group of the row.
   */
  private renderRadioGroup(): TemplateResult {
    return html` <gz-radio-group
      .selectedOption="${this.value}"
      ?disabled="${(!this.indeterminate && !this.checked) || this.hasColumnsDisabled}"
      @radioSelectionChanged="${this.handleRowValueSelectionChange}"
    >
      ${map(this.columnDetails, columnDetail => this.renderCell(columnDetail))}
    </gz-radio-group>`;
  }

  /**
   * Render the cell of the row.
   * - If column is not available, render an empty cell.
   * - If column is available, render radio/checkbox or text based on the selection type.
   * @param columnDetail
   */
  private renderCell(columnDetail: ColumnDetail<Column>): TemplateResult {
    if (this.availableColumns && !this.availableColumns.includes(columnDetail.column)) {
      return html`<div class="view-cell"></div>`;
    }

    // render radio with label if column selection is allowed
    // render only text if column selection is not allowed
    let content: TemplateResult = html``;
    switch (this.selectionType) {
      case SelectionType.RADIO:
        content = this.renderRadioCell(columnDetail);
        break;
      case SelectionType.CHECKBOX:
        content = this.renderCheckboxCell(columnDetail);
        break;
    }

    // render tooltip if available
    return html`<div class="view-cell">
      ${
        columnDetail.tooltip && columnDetail.label
          ? html`<gz-tooltip tooltipText="${columnDetail.tooltip}">${content}</gz-tooltip>`
          : content
      }
      </gz-tooltip>
    </div>`;
  }

  /**
   * Render the radio cell of the row.
   * @param columnDetail
   */
  private renderRadioCell(columnDetail: ColumnDetail<Column>): TemplateResult {
    return html`
      ${this.allowColumnSelection
        ? html`<gz-radio .value="${columnDetail.column}" .label="${columnDetail.label}"></gz-radio>`
        : ''}
      ${!this.allowColumnSelection
        ? html`<gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}"
            >${columnDetail.label}</gz-text
          >`
        : ''}
    `;
  }

  /**
   * Render the checkbox cell of the row.
   * @param columnDetail
   * @private
   */
  private renderCheckboxCell(columnDetail: ColumnDetail<Column>): TemplateResult {
    return html`
      ${this.allowColumnSelection
        ? html`<gz-checkbox
            style="${columnDetail.label ? '' : 'bottom: 7px'}"
            .value="${columnDetail.column}"
            ?disabled="${(!this.indeterminate && !this.checked) || this.hasColumnsDisabled}"
            ?checked="${(this.value ?? []).includes(columnDetail.column)}"
            .label="${columnDetail.label}"
            @change="${e => this.handleRowValueSelectionChange(e, columnDetail.column)}"
          ></gz-checkbox>`
        : ''}
      ${!this.allowColumnSelection
        ? html`<gz-text fontSize="${FontSizes.PARAGRAPH_13}" fontWeight="${FontWeights.NORMAL}"
            >${columnDetail.label}</gz-text
          >`
        : ''}
    `;
  }

  /**
   * Handle the change event of the row checkbox.
   * @param event
   */
  private handleRowCheckboxSelectionChange(event: CustomEvent) {
    event.stopPropagation();
    this.checked = event.detail.checked;
    this.dispatchEvent(new CustomEvent(ComplexSelectionGroupEvent.ROW_CHECKBOX_CHANGE));
  }

  /**
   * Handle the click event of the row radio.
   * @param event
   * @param value
   */
  private handleRowValueSelectionChange(event: CustomEvent, value?: Column) {
    event.stopPropagation();

    let eventDetail = {};

    // compute the new value
    switch (this.selectionType) {
      case SelectionType.RADIO:
        this.value = event.detail.selectedValue;
        break;

      case SelectionType.CHECKBOX:
        if (event.detail.checked) {
          this.value = [...this.value, value];
        } else {
          this.value = this.value.filter(val => val !== value);
        }

        eventDetail = { checked: event.detail.checked, column: value };
        break;
    }

    // dispatch the event
    this.dispatchEvent(
      new CustomEvent(ComplexSelectionGroupEvent.ROW_VALUE_CHANGE, {
        detail: eventDetail,
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }
}
